import { useState, useEffect } from "react";
import {
  createRazorpayOrder,
  verifyRazorpayPayment,
  createCodPayment,
} from "../services/paymentService";

export default function PaymentPage() {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // URL se orderId lo — /payment?orderId=5
  const params   = new URLSearchParams(window.location.search);
  const orderId  = params.get("orderId");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !orderId) { window.location.href = "/"; return; }
    // Razorpay script load karo
    loadRazorpayScript();
  }, []);

  const loadRazorpayScript = () => {
    if (document.getElementById("razorpay-script")) return;
    const script = document.createElement("script");
    script.id  = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    document.body.appendChild(script);
  };

  // ── Razorpay Payment ─────────────────────────────────
  const handleRazorpay = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await createRazorpayOrder(orderId);
      setPaymentData(data);

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: "Savora",
        description: `Order #${orderId}`,
        image: "https://i.ibb.co/placeholder/savora-logo.png",
        order_id: data.razorpayOrderId,

        handler: async (response) => {
          try {
            await verifyRazorpayPayment({
              razorpayOrderId:  response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setSuccess(true);
            setTimeout(() => window.location.href = "/orders", 3000);
          } catch (e) {
            setError("Payment verification failed: " + e.message);
          }
        },

        prefill: {
          name:  localStorage.getItem("fullName") || "",
          email: localStorage.getItem("email")    || "",
        },

        theme: { color: "#f97316" },

        modal: {
          ondismiss: () => {
            setLoading(false);
            setError("Payment cancelled!");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── COD Payment ──────────────────────────────────────
  const handleCod = async () => {
    setLoading(true);
    setError("");
    try {
      await createCodPayment(orderId);
      setSuccess(true);
      setTimeout(() => window.location.href = "/orders", 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Success Screen ───────────────────────────────────
  if (success) return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center px-4">
      <div className="text-center">
        <div className="w-24 h-24 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
          <span className="text-5xl">✅</span>
        </div>
        <h1 className="text-3xl font-black text-white mb-2">Payment Successful!</h1>
        <p className="text-zinc-500 text-sm mb-2">Order #{orderId} confirm ho gaya!</p>
        <p className="text-zinc-600 text-xs">Thodi der mein orders page pe redirect ho jaoge...</p>
      </div>
    </div>
  );

  // ── Payment Page ─────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white flex items-center justify-center px-4">

      {/* Background blobs */}
      <div className="absolute top-[-80px] left-[-80px] w-[300px] h-[300px] rounded-full bg-orange-500 opacity-5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-60px] right-[-60px] w-[250px] h-[250px] rounded-full bg-green-500 opacity-5 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">

        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-4xl">🍽️</span>
          <h1 className="text-3xl font-black text-white mt-2">
            Sav<span className="text-orange-500">ora</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-1 tracking-widest uppercase">Complete your order</p>
        </div>

        {/* Order Summary Card */}
        <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6 mb-4">
          <h2 className="text-xs text-zinc-400 uppercase tracking-wider mb-4">Order Summary</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold">Order #{orderId}</p>
              <p className="text-zinc-500 text-sm mt-0.5">Ready to pay</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-zinc-500">Status</p>
              <span className="text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                PENDING
              </span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-4">
            <p className="text-red-400 text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Payment Options */}
        <div className="bg-[#1a1a1a] border border-zinc-800 rounded-2xl p-6">
          <h2 className="text-xs text-zinc-400 uppercase tracking-wider mb-5">Payment Method</h2>

          <div className="space-y-3">

            {/* Razorpay Option */}
            <button
              onClick={handleRazorpay}
              disabled={loading}
              className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-orange-500/50 rounded-xl p-4 flex items-center gap-4 transition-all group disabled:opacity-50"
            >
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20 shrink-0">
                <span className="text-2xl">💳</span>
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-semibold text-sm group-hover:text-orange-400 transition-colors">
                  Pay Online
                </p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  UPI · Cards · Net Banking · Wallets
                </p>
              </div>
              <span className="text-zinc-600 group-hover:text-orange-400 transition-colors text-lg">→</span>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-zinc-800" />
              <span className="text-zinc-600 text-xs">or</span>
              <div className="flex-1 h-px bg-zinc-800" />
            </div>

            {/* COD Option */}
            <button
              onClick={handleCod}
              disabled={loading}
              className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-green-500/50 rounded-xl p-4 flex items-center gap-4 transition-all group disabled:opacity-50"
            >
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center border border-green-500/20 shrink-0">
                <span className="text-2xl">💵</span>
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-semibold text-sm group-hover:text-green-400 transition-colors">
                  Cash on Delivery
                </p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Delivery pe cash dena hoga
                </p>
              </div>
              <span className="text-zinc-600 group-hover:text-green-400 transition-colors text-lg">→</span>
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-zinc-400 text-sm">Processing...</span>
            </div>
          )}
        </div>

        {/* Back button */}
        <button
          onClick={() => window.history.back()}
          className="w-full mt-4 text-zinc-600 hover:text-zinc-400 text-sm transition-colors py-2"
        >
          ← Wapas jao
        </button>
      </div>
    </div>
  );
}